import { useRef, useState, useEffect } from "react";
import { CheckCircle2, AlertTriangle, Award } from "lucide-react";
import { Button } from "@/components/ui/button";

export function useSyncedView() {
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  return { zoom, setZoom, pan, setPan };
}

export function SyncedImage({ src, alt, view, testId }) {
  const drag = useRef(null);
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const onWheel = (e) => { e.preventDefault(); view.setZoom((z) => Math.min(8, Math.max(1, z - Math.sign(e.deltaY) * 0.25))); };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [view]);
  return (
    <div ref={ref} data-testid={testId} className="relative w-full aspect-[4/5] bg-slate-100 rounded-lg overflow-hidden select-none cursor-grab"
      onMouseDown={(e) => { drag.current = { x: e.clientX - view.pan.x, y: e.clientY - view.pan.y }; }}
      onMouseMove={(e) => { if (drag.current) view.setPan({ x: e.clientX - drag.current.x, y: e.clientY - drag.current.y }); }}
      onMouseUp={() => { drag.current = null; }} onMouseLeave={() => { drag.current = null; }}>
      {src ? <img src={src} alt={alt} draggable={false} className="absolute inset-0 w-full h-full object-contain" style={{ transform: `translate(${view.pan.x}px, ${view.pan.y}px) scale(${view.zoom})`, transformOrigin: "center" }} /> :
        <div className="absolute inset-0 flex items-center justify-center text-xs text-slate-400">—</div>}
    </div>
  );
}

export function CandidatePanel({ title, src, view, cand, isBest, isSelected, onSelect, canSelect, testId }) {
  const m = cand?.metrics;
  return (
    <div data-testid={testId} className={`rounded-xl border p-2 bg-white ${isSelected ? "border-emerald-600 ring-2 ring-emerald-200" : "border-slate-200"}`}>
      <div className="flex items-center justify-between mb-1 min-h-[22px]">
        <span className="text-xs font-semibold text-slate-800 truncate">{title}</span>
        <span className="flex items-center gap-1">
          {isBest && <span data-testid={`${testId}-best`} className="inline-flex items-center gap-0.5 text-[10px] font-semibold bg-emerald-700 text-white rounded-full px-1.5 py-0.5"><Award className="w-3 h-3" /> Best</span>}
          {cand && (cand.valid ? <CheckCircle2 className="w-4 h-4 text-emerald-600" aria-label="validated" /> : <AlertTriangle className="w-4 h-4 text-amber-500" aria-label="failed validation" />)}
        </span>
      </div>
      <SyncedImage src={src} alt={title} view={view} testId={`${testId}-img`} />
      {cand && m && (
        <dl className="grid grid-cols-2 gap-x-2 text-[10px] text-slate-600 mt-2">
          <dt>Score</dt><dd className="text-right font-semibold" data-testid={`${testId}-score`}>{cand.score}</dd>
          <dt>Ridge fidelity (F1)</dt><dd className="text-right">{m.ridge_overlap_f1}</dd>
          <dt>Continuity</dt><dd className="text-right">{m.ridge_continuity_pct}%</dd>
          <dt>Ridge/valley sep.</dt><dd className="text-right">{m.ridge_valley_separation}</dd>
          <dt>Minutiae</dt><dd className="text-right">{m.minutiae_count}{m.minutiae_reference_reliable ? ` / ${m.reference_minutiae_count} ref` : ""}</dd>
          <dt>Self-match</dt><dd className="text-right">{m.self_match_score ?? "n/a"}</dd>
          <dt>Spurious</dt><dd className="text-right">{m.spurious_minutiae_pct == null ? "n/a" : `${m.spurious_minutiae_pct}%`}</dd>
          <dt>NFIQ 2</dt><dd className="text-right">{cand.nfiq2 ?? "N/A"}</dd>
          {cand.ridge_stats && <><dt>Output</dt><dd className="text-right">{cand.ridge_stats.width}×{cand.ridge_stats.height} · duty {cand.ridge_stats.duty_cycle} · ridge {cand.ridge_stats.ridge_width_px}px</dd></>}
        </dl>
      )}
      {cand?.failures?.length > 0 && <ul className="mt-1 text-[10px] text-amber-700 list-disc pl-3">{cand.failures.map((f) => <li key={f}>{f}</li>)}</ul>}
      {canSelect && onSelect && <Button size="sm" variant={isSelected ? "default" : "outline"} data-testid={`${testId}-select`} onClick={onSelect} className="w-full mt-2 h-7 text-xs rounded-lg">{isSelected ? "Selected" : "Select this version"}</Button>}
    </div>
  );
}
